import { useId, useMemo, useState } from 'react';
import { useAuth } from '../auth/AuthProvider';
import { Icon } from '../components/Icon';
import type { Organisation } from '../config';
import { useAppConfig } from '../configContext';
import { strings } from '../strings';

/** Case- and accent-insensitive match on any part of the organisation's name or realm code. */
export function matchOrganisations(organisations: Organisation[], query: string): Organisation[] {
  const fold = (s: string) => s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
  const q = fold(query.trim());
  if (!q) return organisations;
  return organisations.filter((o) => fold(o.name).includes(q) || fold(o.realm.replace(/^endurance-/, '')).includes(q));
}

export function SignIn() {
  const { organisations, supportContact } = useAppConfig();
  const { signIn, rememberedOrganisation, status, error } = useAuth();
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState<Organisation | undefined>(rememberedOrganisation);
  const listId = useId();
  const matches = useMemo(() => matchOrganisations(organisations, query), [organisations, query]);

  if (status === 'redirecting') {
    return (
      <p role="status" className="card">
        {strings.signIn.redirecting}
      </p>
    );
  }

  return (
    <section className="card mx-auto max-w-2xl" aria-labelledby="sign-in-title">
      <h1 id="sign-in-title" className="mt-0">
        {strings.signIn.title}
      </h1>
      <p>{strings.signIn.intro}</p>

      {error && (
        <div role="alert" className="mb-6 flex gap-3 border-l-4 border-status-error bg-white p-4">
          <Icon name="errorWarning" className="mt-1 h-6 w-6 text-brand-magenta" />
          <p className="m-0">{strings.signIn.failed(supportContact)}</p>
        </div>
      )}

      {rememberedOrganisation && (
        <div className="mb-6 flex flex-wrap items-center gap-4 bg-neutral-surface p-4">
          <Icon name="building" className="h-6 w-6 text-brand-magenta" />
          <span>{strings.signIn.remembered(rememberedOrganisation.name)}</span>
          <button type="button" className="btn-primary" onClick={() => void signIn(rememberedOrganisation)}>
            {strings.signIn.continue}
          </button>
        </div>
      )}

      <label htmlFor="org-search" className="block font-bold">
        {strings.signIn.searchLabel}
      </label>
      <input
        id="org-search"
        className="field mt-2"
        type="search"
        autoComplete="organization"
        placeholder={strings.signIn.searchPlaceholder}
        value={query}
        aria-controls={listId}
        onChange={(e) => setQuery(e.target.value)}
      />

      <fieldset className="mt-4 border-0 p-0">
        <legend className="sr-only">{strings.signIn.searchLabel}</legend>
        <ul id={listId} className="m-0 max-h-80 list-none overflow-y-auto border border-neutral-field p-0">
          {matches.map((org) => {
            const checked = selected?.realm === org.realm;
            return (
              <li key={org.realm} className="border-b border-neutral-field last:border-b-0">
                <label
                  className={`flex cursor-pointer items-center gap-3 px-4 py-3 ${checked ? 'bg-neutral-surface font-bold' : ''}`}
                >
                  <input
                    type="radio"
                    name="organisation"
                    value={org.realm}
                    checked={checked}
                    onChange={() => setSelected(org)}
                    className="h-5 w-5 shrink-0 accent-brand-orchid"
                  />
                  {org.name}
                </label>
              </li>
            );
          })}
        </ul>
        {matches.length === 0 && (
          <p role="status" className="mt-2">
            {strings.signIn.noMatch}
          </p>
        )}
      </fieldset>

      <button
        type="button"
        className="btn-primary mt-6"
        disabled={!selected}
        onClick={() => selected && void signIn(selected)}
      >
        {strings.signIn.continue}
      </button>
    </section>
  );
}
